import React, { useEffect, useState } from 'react';
import { getAuthService } from './oidc-client';

interface CallbackPageProps {
  type: 'signin' | 'signout' | 'silent';
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const CallbackPage: React.FC<CallbackPageProps> = ({ type, onSuccess, onError }) => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    let isActive = true;
    const safeSetStatus = (nextStatus: 'processing' | 'success' | 'error', nextMessage: string) => {
      if (!isActive) {
        return;
      }
      setStatus(nextStatus);
      setMessage(nextMessage);
    };

    const handleCallback = async () => {
      const authService = getAuthService();
      if (!authService) {
        const error = new Error('Auth service not initialized');
        safeSetStatus('error', 'Authentication service not available');
        onError?.(error);
        return;
      }

      const callbackKey = `oidc_callback:${type}:${window.location.pathname}:${window.location.search}`;
      const callbackStatus = sessionStorage.getItem(callbackKey);
      if (callbackStatus === 'complete') {
        if (type !== 'silent') {
          safeSetStatus('success', 'Authentication already processed. Redirecting...');
          if (onSuccess) {
            onSuccess();
          } else {
            window.location.href = '/';
          }
        }
        return;
      }
      if (callbackStatus === 'processing') {
        if (type !== 'silent') {
          safeSetStatus('processing', 'Completing authentication...');
        }
        return;
      }
      sessionStorage.setItem(callbackKey, 'processing');

      try {
        switch (type) {
          case 'signin':
            safeSetStatus('processing', 'Completing sign in...');
            await authService.completeLogin();
            safeSetStatus('success', 'Sign in successful! Redirecting...');
            sessionStorage.setItem(callbackKey, 'complete');

            // Store authentication flag for other apps to check
            localStorage.setItem('tagna_authenticated', 'true');
            localStorage.setItem('tagna_login_time', Date.now().toString());

            if (onSuccess) {
              onSuccess();
            } else {
              // Default redirect to home
              window.location.href = '/';
            }
            break;

          case 'signout':
            safeSetStatus('processing', 'Completing sign out...');
            await authService.completeLogout();
            safeSetStatus('success', 'Sign out successful! Redirecting...');
            sessionStorage.setItem(callbackKey, 'complete');

            // Clear authentication flag
            localStorage.removeItem('tagna_authenticated');
            localStorage.removeItem('tagna_login_time');
            sessionStorage.clear();

            if (onSuccess) {
              onSuccess();
            } else {
              // Default redirect to home
              window.location.href = '/';
            }
            break;

          case 'silent':
            // Silent renew handled by iframe, no UI needed
            const userManager = authService.getUserManager();
            await userManager.signinSilentCallback();
            sessionStorage.setItem(callbackKey, 'complete');
            break;

          default:
            throw new Error(`Unknown callback type: ${type}`);
        }
      } catch (error) {
        console.error(`${type} callback error:`, error);
        sessionStorage.removeItem(callbackKey);
        safeSetStatus(
          'error',
          `Authentication ${type} failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
        onError?.(error instanceof Error ? error : new Error('Unknown error'));
      }
    };

    handleCallback();
    return () => {
      isActive = false;
    };
  }, [type, onSuccess, onError]);

  // Silent renew happens in an iframe, don't render anything
  if (type === 'silent') {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {status === 'processing' && (
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          )}
          {status === 'success' && (
            <svg className="h-12 w-12 text-green-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {status === 'error' && (
            <svg className="h-12 w-12 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {type === 'signin' ? 'Signing In' : 'Signing Out'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{message}</p>
        </div>
      </div>
    </div>
  );
};
